import React, { useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
    Box,
    Button,
    Typography,
    Switch,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Stack
} from "@mui/material";
import API from "../api/axiosInstance";

export type AutoFillRow = {
    id: number;
    sourceType: "Dataset" | "Table";
    keyField: string;
    dataSet?: string;
    sourceTable?: string;
    linkedField: string;
    enabled?: boolean;
};

const AutoFillPanel: React.FC<{ tableId: string }> = ({ tableId }) => {
    const theme = useTheme();
    const [rowData, setRowData] = useState<AutoFillRow[]>([]);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [editingRow, setEditingRow] = useState<AutoFillRow | null>(null);
    const [fields, setFields] = useState<string[]>([]);
    const [datasets, setDatasets] = useState<any[]>([]);
    const [tables, setTables] = useState<any[]>([]);
    const [linkedFields, setLinkedFields] = useState<string[]>([]);

    useEffect(() => {
        loadAutoFills();
        fetchFields();
        fetchDatasets();
        fetchTables();
    }, [tableId]);

    const loadAutoFills = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
            const res = await API.get(`/tables/${tableId}/autofills`, headers);
            setRowData(res.data);
        } catch (err) {
            console.error("Error loading autofills", err);
        }
    };

    const fetchFields = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
            const res = await API.get(`/tables/${tableId}`, headers);
            setFields(res.data.fields.map((f: any) => f.fieldName));
        } catch (err) {
            console.error("Failed to fetch fields", err);
        }
    };

    const fetchDatasets = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
            const res = await API.get(`/datasets`, headers);
            setDatasets(res.data);
        } catch (err) {
            console.error("Failed to fetch datasets", err);
        }
    };

    const fetchTables = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
            const res = await API.get(`/tables`, headers);
            setTables(res.data);
        } catch (err) {
            console.error("Failed to fetch tables", err);
        }
    };

    const fetchLinkedFields = async (sourceType: string, sourceId: string) => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
            const endpoint = sourceType === "Dataset" ? `/datasets/${sourceId}/fields` : `/tables/${sourceId}`;
            const res = await API.get(endpoint, headers);
            const fetchedFields = sourceType === "Dataset" ? res.data : res.data.fields;
            setLinkedFields(fetchedFields.map((f: any) => f.fieldName));
        } catch (err) {
            console.error("Failed to fetch linked fields", err);
        }
    };

    const handleSave = async (newData: AutoFillRow) => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
            if (editingRow) {
                await API.put(`/tables/${tableId}/autofills/${editingRow.id}`, newData, headers);
            } else {
                await API.post(`/tables/${tableId}/autofills`, newData, headers);
            }
            setIsPopupOpen(false);
            loadAutoFills();
        } catch (err) {
            console.error("Save error", err);
        }
    };

    const handleDelete = async (data: AutoFillRow) => {
        if (!window.confirm(`Delete auto-fill for ${data.keyField}?`)) return;
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
            await API.delete(`/tables/${tableId}/autofills/${data.id}`, headers);
            loadAutoFills();
        } catch (err) {
            console.error("Delete error", err);
        }
    };

    const columnDefs: ColDef<AutoFillRow>[] = [
        { field: "sourceType", headerName: "Source Type", width: 150 },
        { field: "keyField", headerName: "Key Field", width: 150 },
        { field: "dataSet", headerName: "Data Set", width: 200 },
        { field: "sourceTable", headerName: "Source Table", width: 200 },
        { field: "linkedField", headerName: "Linked Field", width: 200 },
        {
            field: "enabled",
            headerName: "Enabled",
            width: 100,
            cellRenderer: (params: ICellRendererParams) => (
                <Switch checked={params.value} onChange={() => toggleEnabled(params)} color="primary" />
            )
        },
        {
            headerName: "Actions",
            width: 180,
            cellRenderer: (params: ICellRendererParams) => (
                <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => handleEdit(params.data)}>
                        Edit
                    </Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(params.data)}>
                        Delete
                    </Button>
                </Stack>
            )
        }
    ];

    const handleEdit = (row: AutoFillRow) => {
        setEditingRow(row);
        setIsPopupOpen(true);
    };

    const handleAdd = () => {
        setEditingRow(null);
        setIsPopupOpen(true);
    };

    const toggleEnabled = async (params: ICellRendererParams) => {
        const updated = { ...params.data, enabled: !params.data.enabled };
        handleSave(updated);
    };

    return (
        <Box sx={{ p: 2, backgroundColor: theme.palette.background.default, color: theme.palette.text.primary, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Auto-Fill Rules</Typography>
                <Button variant="contained" onClick={handleAdd}>+ Add Auto-Fill</Button>
            </Box>

            <Box className={`ag-theme-${theme.palette.mode === 'dark' ? 'alpine-dark' : 'alpine'}`} sx={{ height: 500, width: "100%" }}>
                <AgGridReact columnDefs={columnDefs} rowData={rowData} rowSelection="single" />
            </Box>

            <Dialog open={isPopupOpen} onClose={() => setIsPopupOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingRow ? "Edit Auto-Fill" : "Add Auto-Fill"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <FormControl fullWidth>
                            <InputLabel>Source Type</InputLabel>
                            <Select
                                value={editingRow?.sourceType || "Dataset"}
                                onChange={(e) => setEditingRow({ ...editingRow!, sourceType: e.target.value as any })}
                            >
                                <MenuItem value="Dataset">Dataset</MenuItem>
                                <MenuItem value="Table">Table</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Key Field</InputLabel>
                            <Select
                                value={editingRow?.keyField || ""}
                                onChange={(e) => setEditingRow({ ...editingRow!, keyField: e.target.value })}
                            >
                                {fields.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                            </Select>
                        </FormControl>
                        {editingRow?.sourceType === "Dataset" && (
                            <FormControl fullWidth>
                                <InputLabel>Data Set</InputLabel>
                                <Select
                                    value={editingRow.dataSet || ""}
                                    onChange={(e) => {
                                        setEditingRow({ ...editingRow!, dataSet: e.target.value });
                                        fetchLinkedFields("Dataset", e.target.value);
                                    }}
                                >
                                    {datasets.map((ds) => <MenuItem key={ds.dataSetID} value={ds.dataSetID}>{ds.dataSetName}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                        {editingRow?.sourceType === "Table" && (
                            <FormControl fullWidth>
                                <InputLabel>Source Table</InputLabel>
                                <Select
                                    value={editingRow.sourceTable || ""}
                                    onChange={(e) => {
                                        setEditingRow({ ...editingRow!, sourceTable: e.target.value });
                                        fetchLinkedFields("Table", e.target.value);
                                    }}
                                >
                                    {tables.map((tbl) => <MenuItem key={tbl.tableID} value={tbl.tableID}>{tbl.tableName}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                        <FormControl fullWidth>
                            <InputLabel>Linked Field</InputLabel>
                            <Select
                                value={editingRow?.linkedField || ""}
                                onChange={(e) => setEditingRow({ ...editingRow!, linkedField: e.target.value })}
                            >
                                {linkedFields.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsPopupOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => handleSave(editingRow!)}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AutoFillPanel;
